/**
 * Pricing page — Sprint B.
 *
 * Owner-only choice surface. Presents three plan cards. Each card offers
 * two actions:
 *   • Subscribe (immediate paid) — Checkout with withTrial=false
 *   • Start 14-day trial (optional) — Checkout with withTrial=true
 *
 * The redirect to Stripe is server-driven — this page never touches the
 * Stripe API or Stripe.js. On the return trip, /billing/success polls the
 * subscription mirror; a Checkout redirect alone never grants entitlement.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Loader2, Sparkles } from 'lucide-react';

import { Button } from '../components/ui/button';
import { useRole } from '../context/RoleContext';
import {
  createCheckoutSession,
  getSubscription,
  PLAN_CATALOG,
  statusLabel,
} from '../services/saasBillingService';

export default function Pricing() {
  const navigate = useNavigate();
  const { role } = useRole();
  const [pending, setPending] = useState(null); // "starter|paid" etc.
  const [current, setCurrent] = useState(null); // subscription mirror
  const isOwner = role === 'owner';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getSubscription();
        if (alive) setCurrent(data);
      } catch (_) {
        // Non-fatal — user may simply have no subscription yet.
      }
    })();
    return () => { alive = false; };
  }, []);

  async function startCheckout(planKey, withTrial) {
    if (!isOwner) {
      toast.error('Only the account owner can manage billing.');
      return;
    }
    const key = `${planKey}|${withTrial ? 'trial' : 'paid'}`;
    setPending(key);
    try {
      const res = await createCheckoutSession({ planKey, withTrial });
      if (res?.url) {
        window.location.assign(res.url);
        return; // leaving page — do not reset pending
      }
      toast.error('Could not start checkout — no session URL returned.');
    } catch (e) {
      if (e.status === 403) {
        toast.error('Only the account owner can subscribe.');
      } else {
        toast.error(e.message || 'Could not start checkout.');
      }
    } finally {
      setPending(null);
    }
  }

  const currentPlanKey = current?.subscription?.planKey || null;
  const currentStatus  = current?.subscription?.status || null;
  const showCurrentBanner = Boolean(currentStatus);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <header className="mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900"
              data-testid="pricing-heading">
            Simple pricing. All the field-service tools.
          </h1>
          <p className="mt-3 text-neutral-600 text-lg max-w-2xl">
            One flat monthly price per business. No per-technician fees, no per-lead fees.
            Start a 14-day trial or subscribe immediately.
          </p>
          {showCurrentBanner && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white px-4 py-1.5 text-sm"
                 data-testid="pricing-current-plan-banner">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Current plan: <b>{currentPlanKey || '—'}</b> · {statusLabel(currentStatus)}</span>
              <button
                type="button"
                className="ml-2 underline underline-offset-2 hover:opacity-80"
                onClick={() => navigate('/billing')}
                data-testid="pricing-goto-billing-btn"
              >
                Manage
              </button>
            </div>
          )}
          {!isOwner && (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                 data-testid="pricing-owner-only-warning">
              Only the account owner can start or change a subscription.
            </div>
          )}
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="pricing-plan-grid">
          {PLAN_CATALOG.map((plan) => {
            const isCurrent = currentPlanKey === plan.planKey;
            const paidKey  = `${plan.planKey}|paid`;
            const trialKey = `${plan.planKey}|trial`;
            const isBusyPaid  = pending === paidKey;
            const isBusyTrial = pending === trialKey;
            return (
              <div
                key={plan.planKey}
                data-testid={`pricing-card-${plan.planKey}`}
                className={[
                  'flex flex-col rounded-2xl border bg-white p-8 transition-all',
                  plan.highlight ? 'border-neutral-900 shadow-lg ring-1 ring-neutral-900' : 'border-neutral-200',
                ].join(' ')}
              >
                {plan.highlight && (
                  <span className="inline-block self-start rounded-full bg-neutral-900 text-white text-xs px-2.5 py-1 mb-3"
                        data-testid={`pricing-badge-${plan.planKey}`}>
                    Most popular
                  </span>
                )}
                <h3 className="text-2xl font-semibold text-neutral-900"
                    data-testid={`pricing-name-${plan.planKey}`}>
                  {plan.name}
                </h3>
                <p className="mt-1 text-sm text-neutral-500">{plan.tagline}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-neutral-900"
                        data-testid={`pricing-price-${plan.planKey}`}>
                    {plan.priceLabel}
                  </span>
                  <span className="text-neutral-500">{plan.cadence}</span>
                </div>
                <div className="mt-3">
                  <span
                    className="inline-flex items-center rounded-md bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-xs font-semibold text-emerald-800"
                    data-testid={`pricing-seats-${plan.planKey}`}
                  >
                    {plan.maxUsers == null ? 'Unlimited users' : `Up to ${plan.maxUsers} users`}
                  </span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                      <Check className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" aria-hidden />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col gap-2">
                  <Button
                    className="w-full h-11"
                    variant={plan.highlight ? 'default' : 'secondary'}
                    disabled={!isOwner || isBusyPaid || isBusyTrial || isCurrent}
                    onClick={() => startCheckout(plan.planKey, false)}
                    data-testid={`pricing-subscribe-btn-${plan.planKey}`}
                  >
                    {isBusyPaid ? <Loader2 className="h-4 w-4 animate-spin" /> : (isCurrent ? 'Current plan' : `Subscribe · ${plan.priceLabel}${plan.cadence}`)}
                  </Button>
                  <Button
                    className="w-full h-11"
                    variant="outline"
                    disabled={!isOwner || isBusyPaid || isBusyTrial || isCurrent}
                    onClick={() => startCheckout(plan.planKey, true)}
                    data-testid={`pricing-trial-btn-${plan.planKey}`}
                  >
                    {isBusyTrial ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start 14-day trial'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-neutral-500 max-w-2xl mx-auto">
          Payment method is required upfront for both immediate subscription and the 14-day trial.
          You can cancel any time from the Billing Portal. Trial cancels end at the trial&apos;s last day —
          you are never charged if you cancel before then.
        </p>
      </div>
    </div>
  );
}
